import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Modal, Pagination, Tooltip } from '@mui/material';
import KeyboardReturnOutlinedIcon from '@mui/icons-material/KeyboardReturnOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import CreateDatabaseModal from './Modal/CreateDatabase';
import { useHistory, useLocation } from 'react-router-dom';
import { GlobalContext } from 'providers/GlobalProvider';
import FilterListIcon from '@mui/icons-material/FilterList';
import MasterTable from './Tables/MasterTable';
import PaginationJump from './common/PaginationJump';
import EntityDBColumns from './Tables/EntityDBColumns';
import EntityDBRow from './Tables/EntityDBRow';
import {
  useDeclinePreviousStatus,
  useGetEntitiesByGroupId,
  useRestorePreviousStatus,
  useRestorePreviousStatusByFlag,
} from 'api/bitedb/entities/entities.hooks';
import {
  EntitySelectionAction,
  useEntitySelectionActions,
} from './common/useEntitySelectionActions';
import NotInterestedOutlinedIcon from '@mui/icons-material/NotInterestedOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import DatabaseCard from './NewComponents/DatabaseCard';
import EntityHubNotificationsTab from './NewComponents/EntityHubNotificationsTab';
import { KpiGroup, KpiTile } from '../ExportHub/kpiTiles';
import DeleteCommonModal from 'components/Monitor/Modals/DeleteModal';
import ChangeStatus from './Modal/ChangeStatus';
import ConfirmStatusChangeModal from './Modal/ConfirmStatusChangeModal';
import DatabaseFilterModal from './Modal/DatabaseFilterModal';
import StatusResetBanner from './Components/StatusResetBanner';
import RestorePreviousByFlagButton from './common/RestorePreviousByFlagButton';
import { enqueueSnackbar } from 'notistack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddIcon from '@mui/icons-material/Add';
import ReviewQColumns from './Tables/ReviewQColumns';
import ReviewQRow from './Tables/ReviewQRow';
import { PaginationParams } from '../../api/bitedb/entities/entities';
import { useQueryClient } from '@tanstack/react-query';
import {
  STATS_QUERY_KEYS,
  useGetEntityDashboardStatsByGroupId,
} from 'api/bitedb/stats/stats.hooks';
import CreditMeterButton from 'pages/credit-plans/CreditMeterButton';

const STATUS_RESET_PREVIOUS_FLAGS = ['quarantined', 'barred', 'cleared'] as const;

function useLocalStorage<T>(key: string, defaultValue: T) {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored !== null ? (JSON.parse(stored) as T) : defaultValue;
    } catch {
      return defaultValue;
    }
  });
  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved =
          typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        try {
          localStorage.setItem(key, JSON.stringify(resolved));
        } catch {}
        return resolved;
      });
    },
    [key],
  );
  return [value, set] as const;
}

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const parseListParam = (params: URLSearchParams, key: string) => {
  const rawValues = params.getAll(key);
  if (!rawValues.length) {
    return [];
  }

  return rawValues
    .flatMap((entry) =>
      entry
        .split(',')
        .map((item) => item.trim()),
    )
    .filter((item) => Boolean(item));
};

const parseRepeatedParam = (params: URLSearchParams, key: string) =>
  params
    .getAll(key)
    .map((entry) => entry.trim())
    .filter(Boolean);

const parseYesNoParam = (value: string | null): 'yes' | 'no' | null =>
  value === 'yes' || value === 'no' ? value : null;

interface DashboardProps {
  databases: any[];
  nrOfEntities: number;
  peopleCount: number;
  isLoading: boolean;
  onRefresh: () => void;
  searchTerm: string;
  setSearchTerm: any;
  processingDatabases?: Set<string>;
  onDatabaseCreated?: (databaseId: string) => void;
  onDatabaseUploadFailed?: (databaseId: string) => void;
  onOfferLinkEntities?: (databaseId: string) => void;
  headerActions?: React.ReactNode;
  headerLeftActions?: React.ReactNode;
  isInteractionDisabled?: boolean;
}

const PeopleAndCompany: React.FC<DashboardProps> = ({
  databases,
  isLoading,
  onRefresh,
  searchTerm,
  setSearchTerm,
  processingDatabases,
  onDatabaseCreated,
  onDatabaseUploadFailed,
  onOfferLinkEntities,
  headerActions,
  headerLeftActions,
  isInteractionDisabled = false,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isTeamAdmin = Boolean(
    cur_user?.selected_team?.attributes?.team_admin?.includes(
      cur_user?.basics?.email,
    ) || cur_user?.role === 'owner',
  );
  const canConfigureEntityAddonOnCreate = Boolean(
    isTeamAdmin && cur_user?.hasEntityAddon,
  );

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const page = parseInt(queryParams.get('page') || '1', 10);
  const limit = parseInt(queryParams.get('limit') || '10', 10);
  const sortBy = queryParams.get('sortBy') || 'hit_count';
  const sortOrder = (queryParams.get('sortOrder') as 'asc' | 'desc') || 'desc';
  const urlSearchTerm = queryParams.get('search') || '';
  const entitySearchTermsFromQuery = useMemo(
    () => parseRepeatedParam(queryParams, 'entitySearch'),
    [queryParams],
  );
  const statusFiltersFromQuery = useMemo(
    () => parseListParam(queryParams, 'statusFilters'),
    [queryParams],
  );
  const riskFiltersFromQuery = useMemo(
    () => parseListParam(queryParams, 'riskFilters'),
    [queryParams],
  );
  const lensFiltersFromQuery = useMemo(
    () => parseListParam(queryParams, 'lensFilters'),
    [queryParams],
  );
  const hitCountRangesFromQuery = useMemo(
    () => parseListParam(queryParams, 'hitCountRanges'),
    [queryParams],
  );
  const nameStartsWithFromQuery = useMemo(() => {
    const rawValues = parseListParam(queryParams, 'nameStartsWith');
    const normalized = rawValues
      .map((value) => value.trim())
      .filter(Boolean)
      .map((value) => value[0]?.toUpperCase())
      .filter(
        (value): value is string => Boolean(value) && /^[A-Z]$/.test(value),
      );
    return Array.from(new Set(normalized));
  }, [queryParams]);
  const hasNewResultsFromQuery = useMemo(
    () => parseYesNoParam(queryParams.get('hasNewResults')),
    [queryParams],
  );
  const statusResetFromQuery = queryParams.get('statusReset') === 'true';

  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [showCreateDatabaseModal, setShowCreateDatabaseModal] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState(urlSearchTerm);
  const [entitySearchTerms, setEntitySearchTerms] = useState<string[]>(
    entitySearchTermsFromQuery,
  );
  const [selectedDB] = useState<string>('');
  const [isDeletingItems, setIsDeletingItems] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [dbPage, setDbPage] = useState(1);
  const [dbSortBy, setDbSortBy] = useLocalStorage(
    'bitedb.entity.sortBy',
    'created_at',
  );
  const [dbSortOrder, setDbSortOrder] = useLocalStorage<'asc' | 'desc'>(
    'bitedb.entity.sortOrder',
    'desc',
  );
  const [viewMode, setViewMode] = useState<'review_queue' | 'all_entities'>(
    'all_entities',
  );
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [statusFilters, setStatusFilters] = useState<string[]>(
    statusFiltersFromQuery,
  );
  const [riskFilters, setRiskFilters] =
    useState<string[]>(riskFiltersFromQuery);
  const [lensFilters, setLensFilters] =
    useState<string[]>(lensFiltersFromQuery);
  const [hitCountRanges, setHitCountRanges] = useState<string[]>(
    hitCountRangesFromQuery,
  );
  const [nameStartsWith, setNameStartsWith] = useState<string[]>(
    nameStartsWithFromQuery,
  );
  const [hasNewResultsFilter, setHasNewResultsFilter] = useState<
    'yes' | 'no' | null
  >(hasNewResultsFromQuery);
  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [expandedEntityIds, setExpandedEntityIds] = useState<
    Record<string, boolean>
  >({});
  const [dbViewMode, setDbViewMode] = useLocalStorage<'grid' | 'list'>(
    'bitedb.entity.viewMode',
    'grid',
  );
  const [activeTab, setActiveTab] = useState<
    'databases' | 'entities-overview' | 'notifications'
  >('databases');
  const showEntityTable = activeTab === 'entities-overview';
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [gridCols, setGridCols] = useState(() => {
    const w = window.innerWidth;
    return w >= 1400 ? 4 : w >= 1024 ? 3 : w >= 640 ? 2 : 1;
  });

  useEffect(() => {
    setStatusFilters(statusFiltersFromQuery);
  }, [statusFiltersFromQuery]);

  useEffect(() => {
    setRiskFilters(riskFiltersFromQuery);
  }, [riskFiltersFromQuery]);

  useEffect(() => {
    setLensFilters(lensFiltersFromQuery);
  }, [lensFiltersFromQuery]);

  useEffect(() => {
    setHitCountRanges(hitCountRangesFromQuery);
  }, [hitCountRangesFromQuery]);

  useEffect(() => {
    setNameStartsWith(nameStartsWithFromQuery);
  }, [nameStartsWithFromQuery]);

  useEffect(() => {
    setEntitySearchTerms(entitySearchTermsFromQuery);
  }, [entitySearchTermsFromQuery]);

  useEffect(() => {
    setLocalSearchTerm(urlSearchTerm);
  }, [urlSearchTerm]);

  useEffect(() => {
    setHasNewResultsFilter(hasNewResultsFromQuery);
  }, [hasNewResultsFromQuery]);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      const cols = w >= 1400 ? 4 : w >= 1024 ? 3 : w >= 640 ? 2 : 1;
      setGridCols((prev) => (prev !== cols ? cols : prev));
    };
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const hasActiveFilters =
    statusFilters.length > 0 ||
    riskFilters.length > 0 ||
    Boolean(hasNewResultsFilter) ||
    lensFilters.length > 0 ||
    hitCountRanges.length > 0 ||
    nameStartsWith.length > 0 ||
    statusResetFromQuery;

  const debouncedSearchTerm = useDebounce(localSearchTerm, 500);
  const effectiveEntitySearchTerms = useMemo(() => {
    const nextTerms = [...entitySearchTerms];
    const trimmedSearch = debouncedSearchTerm.trim();
    if (
      trimmedSearch &&
      !nextTerms.some(
        (term) => term.toLowerCase() === trimmedSearch.toLowerCase(),
      )
    ) {
      nextTerms.push(trimmedSearch);
    }
    return nextTerms;
  }, [debouncedSearchTerm, entitySearchTerms]);

  useEffect(() => {
    setExpandedEntityIds({});
  }, [selectedDB, effectiveEntitySearchTerms, viewMode, hasActiveFilters]);

  const handleUrlChange = useCallback(
    (newParams: Record<string, string | number>) => {
      const updatedParams = new URLSearchParams(location.search);
      Object.entries(newParams).forEach(([key, value]) => {
        if (value) {
          updatedParams.set(key, String(value));
        } else {
          updatedParams.delete(key);
        }
      });
      history.replace({ search: updatedParams.toString() });
    },
    [history, location.search],
  );

  const updateEntitySearchParams = useCallback(
    (nextTerms: string[], transientSearch = '') => {
      const updatedParams = new URLSearchParams(location.search);
      updatedParams.delete('entitySearch');

      nextTerms.forEach((term) => {
        const trimmed = term.trim();
        if (trimmed) {
          updatedParams.append('entitySearch', trimmed);
        }
      });

      const normalizedSearch = transientSearch.trim();
      if (normalizedSearch) {
        updatedParams.set('search', normalizedSearch);
      } else {
        updatedParams.delete('search');
      }

      updatedParams.set('page', '1');
      history.replace({ search: updatedParams.toString() });
    },
    [history, location.search],
  );

  useEffect(() => {
    if (debouncedSearchTerm !== urlSearchTerm) {
      updateEntitySearchParams(entitySearchTerms, debouncedSearchTerm);
    }
  }, [
    debouncedSearchTerm,
    entitySearchTerms,
    updateEntitySearchParams,
    urlSearchTerm,
  ]);

  const handleApplyDatabaseFilters = useCallback(
    ({
      statuses,
      risks,
      hitCountRanges,
      nameStartsWith,
      hasNewResults,
      lenses,
    }: {
      statuses: string[];
      risks: string[];
      hitCountRanges: string[];
      nameStartsWith: string[];
      hasNewResults: 'yes' | 'no' | null;
      lenses: string[];
    }) => {
      setStatusFilters(statuses);
      setRiskFilters(risks);
      setHitCountRanges(hitCountRanges);
      setNameStartsWith(nameStartsWith);
      setHasNewResultsFilter(hasNewResults);
      setLensFilters(lenses);
      handleUrlChange({
        page: 1,
        statusFilters: statuses.length ? statuses.join(',') : '',
        riskFilters: risks.length ? risks.join(',') : '',
        lensFilters: lenses.length ? lenses.join(',') : '',
        hitCountRanges: hitCountRanges.length ? hitCountRanges.join(',') : '',
        nameStartsWith: nameStartsWith.length ? nameStartsWith.join(',') : '',
        hasNewResults: hasNewResults || '',
      });
    },
    [handleUrlChange],
  );

  const paginationParams: PaginationParams = {
    page: page,
    limit: limit,
    sortBy: sortBy,
    sortOrder: sortOrder,
    exclusions:
      viewMode === 'review_queue' ? ['cleared', 'quarantined', 'barred'] : [],
    ...(selectedDB && selectedDB !== '' && { databaseName: selectedDB }),
    statusFilters: statusFiltersFromQuery,
    riskFilters: riskFiltersFromQuery,
    hasNewResults: hasNewResultsFromQuery || undefined,
    statusReset: statusResetFromQuery || undefined,
    lensFilters: lensFiltersFromQuery,
    hitCountRanges: hitCountRangesFromQuery,
    nameStartsWith: nameStartsWithFromQuery,
    entitySearchTerms: effectiveEntitySearchTerms,
  };

  const groupIdForQueries = isInteractionDisabled
    ? ''
    : cur_user?.selected_team?.id ?? '';

  const dbItemsPerPage = gridCols * 3;

  // The grid renders the same team-scoped database list that the create flow
  // optimistically prepends to and the bulk-upload socket events refetch
  // (useGetDatabasesByGroupId, surfaced here via the `databases` prop). Sorting
  // and pagination are applied client-side so a freshly created database shows
  // up immediately — with its processing spinner — instead of waiting for the
  // entity upload to finish. Search is already applied upstream on the prop.
  const sortedDatabases = useMemo(() => {
    const list = Array.isArray(databases) ? [...databases] : [];
    const direction = dbSortOrder === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (dbSortBy === 'name') {
        return (
          direction * String(a?.name ?? '').localeCompare(String(b?.name ?? ''))
        );
      }
      if (dbSortBy === 'nr_people_and_companies') {
        return (
          direction *
          ((parseInt(a?.nr_people_and_companies, 10) || 0) -
            (parseInt(b?.nr_people_and_companies, 10) || 0))
        );
      }
      // created_at (default)
      return (
        direction *
        (new Date(a?.created_at).getTime() - new Date(b?.created_at).getTime())
      );
    });
    return list;
  }, [databases, dbSortBy, dbSortOrder]);

  const dbTotalPages = Math.max(
    1,
    Math.ceil(sortedDatabases.length / dbItemsPerPage),
  );

  const pagedDatabases = useMemo(() => {
    const start = (dbPage - 1) * dbItemsPerPage;
    return sortedDatabases.slice(start, start + dbItemsPerPage);
  }, [sortedDatabases, dbPage, dbItemsPerPage]);

  const dbsLoading = isLoading;

  // Reset to the first page on a new search; clamp the page when the sorted set
  // shrinks below the current page.
  useEffect(() => {
    setDbPage(1);
  }, [searchTerm]);

  useEffect(() => {
    setDbPage((prev) => Math.min(prev, dbTotalPages));
  }, [dbTotalPages]);

  const entityQueryGroupId = showEntityTable ? groupIdForQueries : '';

  const {
    data: entitiesData,
    pagination: entitiesPagination,
    isLoading: isEntitiesLoading,
    error: entitiesError,
  } = useGetEntitiesByGroupId(
    entityQueryGroupId,
    debouncedSearchTerm,
    paginationParams,
  );

  const isEntityHierarchyContextMode =
    effectiveEntitySearchTerms.length > 0 || hasActiveFilters;

  const isEntityStatusFlatMode =
    viewMode === 'review_queue' &&
    effectiveEntitySearchTerms.length === 0 &&
    !hasActiveFilters;

  const handleAddEntitySearchTerm = useCallback(() => {
    const trimmedTerm = localSearchTerm.trim();
    if (!trimmedTerm) {
      return;
    }

    const nextTerms = entitySearchTerms.some(
      (term) => term.toLowerCase() === trimmedTerm.toLowerCase(),
    )
      ? entitySearchTerms
      : [...entitySearchTerms, trimmedTerm];

    setEntitySearchTerms(nextTerms);
    setLocalSearchTerm('');
    updateEntitySearchParams(nextTerms, '');
  }, [entitySearchTerms, localSearchTerm, updateEntitySearchParams]);

  const handleRemoveEntitySearchTerm = useCallback(
    (termToRemove: string) => {
      const nextTerms = entitySearchTerms.filter(
        (term) => term.toLowerCase() !== termToRemove.toLowerCase(),
      );
      setEntitySearchTerms(nextTerms);
      updateEntitySearchParams(nextTerms, localSearchTerm);
    },
    [entitySearchTerms, localSearchTerm, updateEntitySearchParams],
  );

  const processedTableData = useMemo(() => {
    if (!isEntityHierarchyContextMode || !entitiesData) {
      return entitiesData || [];
    }

    const entities = entitiesData as any[];
    const childrenByParent: Record<string, any[]> = {};
    const topLevelEntityIds = new Set<string>();

    for (const entity of entities) {
      const entityId = entity.entity_id || entity.id;
      if (entity.is_top_level || !entity.parent_entity_id) {
        if (entityId) {
          topLevelEntityIds.add(String(entityId));
        }
        continue;
      }

      const parentId = String(entity.parent_entity_id);
      if (!childrenByParent[parentId]) {
        childrenByParent[parentId] = [];
      }
      childrenByParent[parentId].push(entity);
    }

    if (Object.keys(childrenByParent).length === 0) {
      return entitiesData;
    }

    const result: any[] = [];
    const addedEntityIds = new Set<string>();

    for (const entity of entities) {
      const entityId = entity.entity_id || entity.id;
      const normalizedEntityId = entityId ? String(entityId) : '';
      if (!normalizedEntityId || addedEntityIds.has(normalizedEntityId)) {
        continue;
      }

      const isTopLevel = entity.is_top_level || !entity.parent_entity_id;
      if (isTopLevel) {
        const matchedChildren = childrenByParent[normalizedEntityId];
        if (matchedChildren?.length) {
          result.push({
            ...entity,
            _searchMatchedChildren: matchedChildren,
          });
        } else {
          result.push(entity);
        }
        addedEntityIds.add(normalizedEntityId);
        continue;
      }

      const parentId = entity.parent_entity_id
        ? String(entity.parent_entity_id)
        : '';

      if (parentId && topLevelEntityIds.has(parentId)) {
        addedEntityIds.add(normalizedEntityId);
        continue;
      }

      result.push(entity);
      addedEntityIds.add(normalizedEntityId);
    }

    return result;
  }, [isEntityHierarchyContextMode, entitiesData]);

  const displayedRowsForSelection = entitiesData || [];

  const toggleEntityExpand = useCallback((entityId: string) => {
    if (!entityId) return;
    setExpandedEntityIds((prev) => ({ ...prev, [entityId]: !prev[entityId] }));
  }, []);

  const {
    removeDatabaseEntitiesMutation,
    updateDatabaseEntitiesStatusMutation,
    runSelectionAction,
  } = useEntitySelectionActions(cur_user?.selected_team?.id);
  const restorePreviousStatusMutation = useRestorePreviousStatus(
    undefined,
    cur_user?.selected_team?.id,
  );
  const declinePreviousStatusMutation = useDeclinePreviousStatus(
    undefined,
    cur_user?.selected_team?.id,
  );
  const restorePreviousStatusByFlagMutation = useRestorePreviousStatusByFlag(
    undefined,
    cur_user?.selected_team?.id,
  );
  const [loadingRestoreByFlag, setLoadingRestoreByFlag] = useState<
    (typeof STATUS_RESET_PREVIOUS_FLAGS)[number] | null
  >(null);
  const [pendingRestoreByFlag, setPendingRestoreByFlag] = useState<
    (typeof STATUS_RESET_PREVIOUS_FLAGS)[number] | null
  >(null);
  const isStatusUpdating = updateDatabaseEntitiesStatusMutation.isPending;

  const groupId = cur_user?.selected_team?.id ?? '';
  const statusResetBannerStorageKey = `bdb-status-reset-banner-dismissed:${groupId}`;
  const [statusResetBannerDismissedCount, setStatusResetBannerDismissedCount] =
    useState(() => {
      try {
        return Number(localStorage.getItem(statusResetBannerStorageKey) || '0');
      } catch {
        return 0;
      }
    });

  useEffect(() => {
    try {
      setStatusResetBannerDismissedCount(
        Number(localStorage.getItem(statusResetBannerStorageKey) || '0'),
      );
    } catch {
      setStatusResetBannerDismissedCount(0);
    }
  }, [statusResetBannerStorageKey]);

  // Only the Entities Overview reads these; the Databases and Notifications
  // tabs don't, so they send no dashboard stats request.
  const { data: stats } = useGetEntityDashboardStatsByGroupId(
    showEntityTable ? groupIdForQueries : '',
  );

  const statusResetCount = Number(stats?.total_status_reset_entities || 0);
  const previousStatusRestoreFlags = useMemo(
    () =>
      STATUS_RESET_PREVIOUS_FLAGS.filter((flag) => {
        if (flag === 'quarantined') {
          return Number(stats?.total_status_reset_quarantined_entities || 0) > 0;
        }
        if (flag === 'barred') {
          return Number(stats?.total_status_reset_barred_entities || 0) > 0;
        }
        return Number(stats?.total_status_reset_cleared_entities || 0) > 0;
      }),
    [stats],
  );
  const showStatusResetBanner =
    statusResetCount > 0 && statusResetCount > statusResetBannerDismissedCount;
  const statusResetChipCount = statusResetFromQuery
    ? entitiesPagination?.totalItems ?? statusResetCount
    : statusResetCount;

  const selectedWithPreviousStatus = useMemo(
    () =>
      selectedItems.filter(
        (item) => item?.previous_status_flag && item?.association_id,
      ),
    [selectedItems],
  );

  const invalidateStatsAfterPreviousStatus = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [STATS_QUERY_KEYS.stats],
      exact: false,
    });
  }, [queryClient]);

  const handleReviewStatusResets = useCallback(() => {
    if (isInteractionDisabled) {
      return;
    }
    handleUrlChange({ statusReset: 'true', page: 1 });
  }, [handleUrlChange, isInteractionDisabled]);

  const handleDismissStatusResetBanner = useCallback(() => {
    if (isInteractionDisabled) {
      return;
    }
    try {
      localStorage.setItem(statusResetBannerStorageKey, String(statusResetCount));
    } catch {}
    setStatusResetBannerDismissedCount(statusResetCount);
  }, [
    isInteractionDisabled,
    statusResetBannerStorageKey,
    statusResetCount,
  ]);

  const handleClearStatusResetFilter = useCallback(() => {
    if (isInteractionDisabled) {
      return;
    }
    handleUrlChange({ statusReset: '', page: 1 });
  }, [handleUrlChange, isInteractionDisabled]);

  const handleBulkRestorePrevious = useCallback(() => {
    const ids = selectedWithPreviousStatus.map((item) =>
      String(item.association_id),
    );
    if (!ids.length) {
      return;
    }
    restorePreviousStatusMutation.mutate(ids, {
      onSuccess: (result) => {
        enqueueSnackbar(
          result?.message ||
            `Restored previous status for ${result?.data?.updatedCount ?? ids.length} entit${
              (result?.data?.updatedCount ?? ids.length) === 1 ? 'y' : 'ies'
            }`,
          { variant: 'success' },
        );
        setSelectedItems([]);
        invalidateStatsAfterPreviousStatus();
      },
      onError: (error: any) => {
        enqueueSnackbar(
          error?.message || 'Failed to restore previous status',
          { variant: 'error' },
        );
      },
    });
  }, [
    invalidateStatsAfterPreviousStatus,
    restorePreviousStatusMutation,
    selectedWithPreviousStatus,
  ]);

  const handleBulkDeclinePrevious = useCallback(() => {
    const ids = selectedWithPreviousStatus.map((item) =>
      String(item.association_id),
    );
    if (!ids.length) {
      return;
    }
    declinePreviousStatusMutation.mutate(ids, {
      onSuccess: (result) => {
        enqueueSnackbar(
          result?.message ||
            `Declined previous status for ${result?.data?.updatedCount ?? ids.length} entit${
              (result?.data?.updatedCount ?? ids.length) === 1 ? 'y' : 'ies'
            }`,
          { variant: 'success' },
        );
        setSelectedItems([]);
        invalidateStatsAfterPreviousStatus();
      },
      onError: (error: any) => {
        enqueueSnackbar(
          error?.message || 'Failed to decline previous status',
          { variant: 'error' },
        );
      },
    });
  }, [
    declinePreviousStatusMutation,
    invalidateStatsAfterPreviousStatus,
    selectedWithPreviousStatus,
  ]);

  const getPreviousStatusRestoreCount = useCallback(
    (flag: (typeof STATUS_RESET_PREVIOUS_FLAGS)[number]) => {
      if (flag === 'quarantined') {
        return Number(stats?.total_status_reset_quarantined_entities || 0);
      }
      if (flag === 'barred') {
        return Number(stats?.total_status_reset_barred_entities || 0);
      }
      return Number(stats?.total_status_reset_cleared_entities || 0);
    },
    [stats],
  );

  const requestRestorePreviousByFlag = useCallback(
    (previousStatusFlag: (typeof STATUS_RESET_PREVIOUS_FLAGS)[number]) => {
      if (!groupId || loadingRestoreByFlag) {
        return;
      }
      setPendingRestoreByFlag(previousStatusFlag);
    },
    [groupId, loadingRestoreByFlag],
  );

  const closeRestoreByFlagConfirmation = useCallback(() => {
    if (loadingRestoreByFlag) {
      return;
    }
    setPendingRestoreByFlag(null);
  }, [loadingRestoreByFlag]);

  const pendingRestoreByFlagCount = pendingRestoreByFlag
    ? getPreviousStatusRestoreCount(pendingRestoreByFlag)
    : 0;
  const pendingRestoreByFlagLabel = pendingRestoreByFlag
    ? pendingRestoreByFlag.charAt(0).toUpperCase() +
      pendingRestoreByFlag.slice(1)
    : '';
  const pendingRestoreByFlagDescription = pendingRestoreByFlag
    ? `This will restore ${pendingRestoreByFlagCount} ${
        pendingRestoreByFlagCount === 1 ? 'entity' : 'entities'
      } that ${
        pendingRestoreByFlagCount === 1 ? 'was' : 'were'
      } previously ${pendingRestoreByFlagLabel}.`
    : undefined;

  const handleRestorePreviousByFlag = useCallback(async () => {
    const previousStatusFlag = pendingRestoreByFlag;
    if (!previousStatusFlag || !groupId || loadingRestoreByFlag) {
      return;
    }
    setLoadingRestoreByFlag(previousStatusFlag);
    try {
      const result = await restorePreviousStatusByFlagMutation.mutateAsync({
        groupId: groupId,
        previous_status_flag: previousStatusFlag,
      });
      enqueueSnackbar(
        result?.message || `Restored all that were ${previousStatusFlag}`,
        { variant: 'success' },
      );
      setSelectedItems([]);
      invalidateStatsAfterPreviousStatus();
      setPendingRestoreByFlag(null);
    } catch (error: any) {
      enqueueSnackbar(
        error?.message ||
          `Failed to restore entities that were ${previousStatusFlag}`,
        { variant: 'error' },
      );
    } finally {
      setLoadingRestoreByFlag(null);
    }
  }, [
    groupId,
    invalidateStatsAfterPreviousStatus,
    loadingRestoreByFlag,
    pendingRestoreByFlag,
    restorePreviousStatusByFlagMutation,
  ]);

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  useEffect(() => {
    if (entitiesError) {
      enqueueSnackbar('Failed to fetch entities', { variant: 'error' });
    }
  }, [entitiesError]);

  const handleSelectAll = () => {
    if (isInteractionDisabled) {
      return;
    }
    setSelectedItems(
      selectedItems.length === displayedRowsForSelection.length
        ? []
        : displayedRowsForSelection,
    );
  };

  const handleSort = (key: string) => {
    if (isInteractionDisabled) {
      return;
    }
    const newSortOrder = sortBy === key && sortOrder === 'asc' ? 'desc' : 'asc';
    handleUrlChange({ sortBy: key, sortOrder: newSortOrder });
  };

  const handleEntityPageChange = (
    event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    handleUrlChange({ page: newPage });
  };

  const getParagraphText = (database: any) => {
    if (!database.last_screen_timestamp) return 'Never updated';

    const lastScreenedDate = new Date(database.last_screen_timestamp);
    const today = new Date();

    const lastDay = new Date(
      lastScreenedDate.getFullYear(),
      lastScreenedDate.getMonth(),
      lastScreenedDate.getDate(),
    );
    const todayDay = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    const diffDays = Math.round(
      (todayDay.getTime() - lastDay.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffDays === 0) return 'Updated today';
    return `Updated ${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  const handleBulkAction = async (
    action: EntitySelectionAction,
    status?: string,
  ) => {
    if (isInteractionDisabled) {
      return;
    }
    await runSelectionAction(selectedItems, action, status, () => {
      setSelectedItems([]);
      setIsDeletingItems(false);
      setIsChangingStatus(false);
      setShowStatusConfirmModal(false);
      setPendingStatus(null);
    });
  };

  const handleDeleteEntity = async () => {
    await handleBulkAction('delete');
  };

  const handleEdit = async (item: any) => {
    if (isInteractionDisabled) {
      return;
    }
    setSelectedItems((prevSelected: any[]) => {
      const safePrevSelected = Array.isArray(prevSelected) ? prevSelected : [];
      const alreadySelected = safePrevSelected.some((i) => i.id === item.id);
      if (alreadySelected) {
        return safePrevSelected;
      }
      return [...safePrevSelected, item];
    });
    setIsChangingStatus(true);
  };

  const handleDelete = async (item: any) => {
    if (isInteractionDisabled) {
      return;
    }
    setSelectedItems((prevSelected: any[]) => {
      const safePrevSelected = Array.isArray(prevSelected) ? prevSelected : [];
      const alreadySelected = safePrevSelected.some((i) => i.id === item.id);
      return alreadySelected
        ? safePrevSelected.filter((i) => i.id !== item.id)
        : [...safePrevSelected, item];
    });
    setIsDeletingItems(true);
  };

  const handleStatusSelection = (status: string) => {
    if (isInteractionDisabled) {
      return;
    }
    if (!status) {
      enqueueSnackbar('Please select a status', { variant: 'warning' });
      return;
    }
    if (selectedItems.length === 0) {
      enqueueSnackbar('Please select at least one entity', {
        variant: 'error',
      });
      return;
    }
    setPendingStatus(status);
    setShowStatusConfirmModal(true);
    setIsChangingStatus(false);
  };

  const closeStatusConfirmation = () => {
    setShowStatusConfirmModal(false);
    setPendingStatus(null);
  };

  const confirmStatusChange = () => {
    if (!pendingStatus) {
      return;
    }
    handleBulkAction('updateStatus', pendingStatus);
  };

  return (
    <div className='team-content-container' style={{ paddingTop: '0px' }}>
      <div className='people-col'>
        <div className='team-content-header'>
          <div className='team-content-search people-col'>
            <h1 className='bdb-nc-page-title'>Entity Hub</h1>
            <p className='bdb-nc-page-subtitle'>
              Manage your suppliers, partners, and other business relationships.
            </p>
          </div>

          <div className='module-header-title-conts'>
            <div
              className='module-header-btns'
              style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
            >
              {headerLeftActions}
              {headerActions}
              <CreditMeterButton packType='database' size='sm' />
            </div>
          </div>
        </div>
      </div>

      <nav
        className='export-hub-tabs'
        aria-label='Entity Hub sections'
        style={{ width: '100%', marginTop: 0 }}
      >
        {(
          [
            { key: 'databases', label: 'Databases' },
            { key: 'entities-overview', label: 'Entities Overview' },
            { key: 'notifications', label: 'Notifications' },
          ] as const
        ).map((t) => (
          <button
            key={t.key}
            type='button'
            className={`export-hub-tab ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {activeTab === 'entities-overview' && (
        <div className='eh-dash' style={{ marginTop: '20px', width: '100%' }}>
          <div className='eh-kpi-groups eh-kpi-overview'>
            <KpiGroup>
              <KpiTile
                label='All Entities'
                value={stats?.total_entities ?? 0}
                onClick={
                  isInteractionDisabled
                    ? undefined
                    : () => {
                        setViewMode('all_entities');
                        handleUrlChange({ page: 1 });
                      }
                }
              />
              <KpiTile
                label='Review Queue'
                value={
                  (stats?.total_not_reviewed_entities || 0) +
                  (stats?.total_new_entities || 0)
                }
                tone='warning'
                onClick={
                  isInteractionDisabled
                    ? undefined
                    : () => {
                        setViewMode('review_queue');
                        handleUrlChange({ page: 1 });
                      }
                }
              />
              <KpiTile
                label='All Cleared'
                value={stats?.total_cleared_entities ?? 0}
                tone='success'
                onClick={
                  isInteractionDisabled
                    ? undefined
                    : () =>
                        history.push({
                          pathname: '/stats-roll-up',
                          search: '?status_flag=cleared',
                          state: { status_flag: 'cleared' },
                        })
                }
              />
              <KpiTile
                label='All Quarantined'
                value={stats?.total_quarantined_entities ?? 0}
                tone='warning'
                onClick={
                  isInteractionDisabled
                    ? undefined
                    : () =>
                        history.push({
                          pathname: '/stats-roll-up',
                          search: '?status_flag=quarantined',
                          state: { status_flag: 'quarantined' },
                        })
                }
              />
              <KpiTile
                label='All Barred'
                value={stats?.total_barred_entities ?? 0}
                tone='danger'
                onClick={
                  isInteractionDisabled
                    ? undefined
                    : () =>
                        history.push({
                          pathname: '/stats-roll-up',
                          search: '?status_flag=barred',
                          state: { status_flag: 'barred' },
                        })
                }
              />
            </KpiGroup>
          </div>
        </div>
      )}
      {showEntityTable && viewMode === 'review_queue' && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <div
            className='api-container-left-h1'
            id='your-review-queue-section'
            style={{ paddingTop: '0px' }}
          >
            Your Review Queue
          </div>
        </div>
      )}
      {showEntityTable &&
        (showStatusResetBanner ||
          statusResetFromQuery ||
          previousStatusRestoreFlags.length > 0) && (
          <div className='bdb-status-reset-stack'>
            {showStatusResetBanner && (
              <StatusResetBanner
                count={statusResetCount}
                onReview={handleReviewStatusResets}
                onDismiss={handleDismissStatusResetBanner}
                disabled={isInteractionDisabled}
              />
            )}
            {(statusResetFromQuery ||
              previousStatusRestoreFlags.length > 0) && (
              <div className='bdb-status-reset-actions-row'>
                {statusResetFromQuery && (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 10px',
                      borderRadius: '999px',
                      background: 'rgba(245, 158, 11, 0.12)',
                      border: '1px solid rgba(245, 158, 11, 0.28)',
                      color: 'var(--text-primary)',
                      fontSize: '13px',
                      lineHeight: 1,
                    }}
                  >
                    <span>
                      Status reset by screening · {statusResetChipCount} ×
                    </span>
                    <button
                      type='button'
                      onClick={handleClearStatusResetFilter}
                      disabled={isInteractionDisabled}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'transparent',
                        border: 0,
                        color: 'var(--text-secondary)',
                        cursor: isInteractionDisabled
                          ? 'not-allowed'
                          : 'pointer',
                        opacity: isInteractionDisabled ? 0.6 : 1,
                        padding: 0,
                      }}
                      aria-label='Clear status reset filter'
                    >
                      <CloseOutlinedIcon sx={{ fontSize: 16 }} />
                    </button>
                  </div>
                )}
                {previousStatusRestoreFlags.map((flag) => (
                  <RestorePreviousByFlagButton
                    key={flag}
                    flag={flag}
                    loading={loadingRestoreByFlag === flag}
                    disabled={
                      isInteractionDisabled ||
                      (loadingRestoreByFlag !== null &&
                        loadingRestoreByFlag !== flag)
                    }
                    onClick={() => requestRestorePreviousByFlag(flag)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      {showEntityTable && (
        <div
          className='team-content-header'
          style={{ alignItems: 'center', gap: '12px' }}
        >
          <div
            className='team-content-search'
            style={{ flex: 1, minWidth: 0, height: 'auto', marginBottom: 0 }}
          >
            <div
              className='screening-input-field-container'
              style={{
                width: '100%',
                minHeight: '40px',
                height: '40px',
                alignItems: 'center',
                gap: '10px',
                padding: '7px 12px',
                flexWrap: 'nowrap',
                overflow: 'hidden',
              }}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='20'
                height='20'
                viewBox='0 0 20 20'
                fill='none'
              >
                <path
                  d='M17.5 17.5L14.5834 14.5833M16.6667 9.58333C16.6667 13.4954 13.4954 16.6667 9.58333 16.6667C5.67132 16.6667 2.5 13.4954 2.5 9.58333C2.5 5.67132 5.67132 2.5 9.58333 2.5C13.4954 2.5 16.6667 5.67132 16.6667 9.58333Z'
                  stroke='var(--card-text)'
                  strokeWidth='1.66667'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flex: 1,
                  minWidth: 0,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  whiteSpace: 'nowrap',
                  scrollbarWidth: 'none',
                }}
              >
                {entitySearchTerms.map((term) => (
                  <div
                    key={term}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 10px',
                      borderRadius: '999px',
                      background: 'rgba(72, 149, 239, 0.12)',
                      border: '1px solid rgba(72, 149, 239, 0.28)',
                      color: 'var(--text-primary)',
                      fontSize: '13px',
                      lineHeight: 1,
                      flex: '0 0 auto',
                      maxWidth: '220px',
                    }}
                  >
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {term}
                    </span>
                    <button
                      type='button'
                      onClick={() => handleRemoveEntitySearchTerm(term)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'transparent',
                        border: 0,
                        color: 'var(--text-secondary)',
                        cursor: 'pointer',
                        padding: 0,
                        flex: '0 0 auto',
                      }}
                      aria-label={`Remove ${term} search`}
                    >
                      <CloseOutlinedIcon sx={{ fontSize: 16 }} />
                    </button>
                  </div>
                ))}
                <input
                  placeholder='Search People & Companies'
                  id='primary'
                  type='text'
                  className='screening-input-field-content'
                  name='primary'
                  value={localSearchTerm}
                  style={{
                    flex: 1,
                    minWidth: entitySearchTerms.length ? '180px' : '240px',
                    height: '24px',
                  }}
                  onChange={(e) => {
                    setLocalSearchTerm(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddEntitySearchTerm();
                    } else if (e.key === 'Backspace' && !localSearchTerm) {
                      const lastTerm =
                        entitySearchTerms[entitySearchTerms.length - 1];
                      if (lastTerm) {
                        handleRemoveEntitySearchTerm(lastTerm);
                      }
                    }
                  }}
                />
              </div>
              <Tooltip
                title='Press Enter to add this search as a pinned filter'
                placement='top'
                arrow
              >
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--text-secondary)',
                  }}
                >
                  <KeyboardReturnOutlinedIcon sx={{ fontSize: 18 }} />
                </div>
              </Tooltip>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              flex: '0 0 auto',
              gap: '10px',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <Tooltip title='Filter' placement='bottom-start' arrow>
              <div
                className={`audit-btn-star${
                  hasActiveFilters ? ' selected' : ''
                }`}
                onClick={() => {
                  if (!isInteractionDisabled) {
                    setShowFilterModal(true);
                  }
                }}
                style={{
                  opacity: isInteractionDisabled ? 0.6 : 1,
                  cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                <FilterListIcon
                  sx={{
                    fontSize: 20,
                    color: hasActiveFilters
                      ? 'var(--Primary-400, #4895ef)'
                      : 'var(--card-text)',
                  }}
                />
              </div>
            </Tooltip>
            <Tooltip
              title={'Update Status'}
              placement='bottom-start'
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    display: 'flex',
                    padding: 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    alignSelf: 'stretch',
                    borderRadius: 'var(--radius-md, 8px)',
                    background: 'var(--Background-200, var(--bg-colors))',
                    color: 'var(--text-prim25)',
                    maxWidth: '700px',
                    '& .MuiTooltip-arrow': {
                      color: 'var(--Background-200, var(--bg-colors))',
                    },
                  },
                },
              }}
            >
              <div
                className='audit-btn-star'
                onClick={() => {
                  if (!isInteractionDisabled) {
                    setIsChangingStatus(true);
                  }
                }}
                style={{
                  opacity: isInteractionDisabled ? 0.6 : 1,
                  cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                <EditOutlinedIcon
                  sx={{ fontSize: 20, color: 'var(--card-text)' }}
                />
              </div>
            </Tooltip>
            {selectedWithPreviousStatus.length > 0 && (
              <>
                <Tooltip
                  title='Restore previous status'
                  placement='bottom-start'
                  arrow
                >
                  <div
                    className='audit-btn-star'
                    onClick={() => {
                      if (
                        !isInteractionDisabled &&
                        !restorePreviousStatusMutation.isPending
                      ) {
                        handleBulkRestorePrevious();
                      }
                    }}
                    style={{
                      opacity:
                        isInteractionDisabled ||
                        restorePreviousStatusMutation.isPending
                          ? 0.6
                          : 1,
                      cursor:
                        isInteractionDisabled ||
                        restorePreviousStatusMutation.isPending
                          ? 'not-allowed'
                          : 'pointer',
                    }}
                  >
                    <ReplayOutlinedIcon
                      sx={{ fontSize: 20, color: 'var(--card-text)' }}
                    />
                  </div>
                </Tooltip>
                <Tooltip
                  title='Decline previous status'
                  placement='bottom-start'
                  arrow
                >
                  <div
                    className='audit-btn-star'
                    onClick={() => {
                      if (
                        !isInteractionDisabled &&
                        !declinePreviousStatusMutation.isPending
                      ) {
                        handleBulkDeclinePrevious();
                      }
                    }}
                    style={{
                      opacity:
                        isInteractionDisabled ||
                        declinePreviousStatusMutation.isPending
                          ? 0.6
                          : 1,
                      cursor:
                        isInteractionDisabled ||
                        declinePreviousStatusMutation.isPending
                          ? 'not-allowed'
                          : 'pointer',
                    }}
                  >
                    <NotInterestedOutlinedIcon
                      sx={{ fontSize: 20, color: 'var(--card-text)' }}
                    />
                  </div>
                </Tooltip>
              </>
            )}
            <Tooltip
              title={'Remove from Database'}
              placement='bottom-start'
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    display: 'flex',
                    padding: 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    alignSelf: 'stretch',
                    borderRadius: 'var(--radius-md, 8px)',
                    background: 'var(--Background-200, var(--bg-colors))',
                    color: 'var(--text-prim25)',
                    maxWidth: '700px',
                    '& .MuiTooltip-arrow': {
                      color: 'var(--Background-200, var(--bg-colors))',
                    },
                  },
                },
              }}
            >
              <div
                className='audit-btn-star'
                onClick={() => {
                  if (!isInteractionDisabled) {
                    setIsDeletingItems(true);
                  }
                }}
                style={{
                  opacity: isInteractionDisabled ? 0.6 : 1,
                  cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                >
                  <path
                    d='M7.5 2.5H12.5M2.5 5H17.5M15.8333 5L15.2489 13.7661C15.1612 15.0813 15.1174 15.7389 14.8333 16.2375C14.5833 16.6765 14.206 17.0294 13.7514 17.2497C13.235 17.5 12.5759 17.5 11.2578 17.5H8.74221C7.42409 17.5 6.76503 17.5 6.24861 17.2497C5.79396 17.0294 5.41674 16.6765 5.16665 16.2375C4.88259 15.7389 4.83875 15.0813 4.75107 13.7661L4.16667 5M8.33333 8.75V12.9167M11.6667 8.75V12.9167'
                    stroke='var(--card-text)'
                    strokeWidth='1.66667'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
            </Tooltip>
          </div>
        </div>
      )}

      {showEntityTable && (
        <MasterTable
          cols={
            <ReviewQColumns
              handleCheckboxClick={handleSelectAll}
              hideCountry
              disabled={isInteractionDisabled}
              isSelectedItems={
                selectedItems.length === displayedRowsForSelection.length &&
                displayedRowsForSelection.length > 0
              }
              sortData={handleSort}
              sortConfig={{ key: sortBy, direction: sortOrder }}
            />
          }
          loading={isEntitiesLoading}
          suppliersData={processedTableData}
          location={'/supplier'}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          currentPage={page}
          totalPages={entitiesPagination?.totalPages || 1}
          onPageChange={handleEntityPageChange}
          customRowComponent={(props: any) => (
            <ReviewQRow
              location={'/supplier'}
              viewType='Entity View'
              supplier={props.supplier}
              selectedItems={props.selectedItems}
              setSelectedItems={props.setSelectedItems}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              hideCountry
              hasChildren={
                !isEntityStatusFlatMode && Boolean(props.supplier?.has_children)
              }
              isExpanded={
                isEntityStatusFlatMode
                  ? false
                  : isEntityHierarchyContextMode
                  ? Boolean(
                      props.supplier?._searchMatchedChildren?.length ||
                        props.supplier?._contextChildren?.length,
                    )
                  : Boolean(
                      expandedEntityIds[
                        props.supplier?.entity_id || props.supplier?.id
                      ],
                    )
              }
              onToggleExpand={() =>
                toggleEntityExpand(
                  props.supplier?.entity_id || props.supplier?.id,
                )
              }
              databaseId={props.supplier?.database_id}
              isContextParent={
                !isEntityStatusFlatMode &&
                Boolean(props.supplier?._isContextParent)
              }
              isSearchMode={isEntityHierarchyContextMode}
              searchMatchedChildren={
                props.supplier?._searchMatchedChildren ||
                props.supplier?._contextChildren
              }
              expandedEntityIds={expandedEntityIds}
              onToggleEntityExpand={toggleEntityExpand}
              disabled={isInteractionDisabled}
            />
          )}
        />
      )}

      {activeTab === 'databases' && (
        <div
          className='api-container'
          style={{
            gap: '10px',
            padding: '0px 0px 0px 0px',
            width: '100%',
            maxHeight: 'none',
          }}
        >
          <div
            className='api-container-left'
            style={{ flex: '1', gap: '20px', overflow: 'visible' }}
          >
            <div
              className='team-content-header'
              style={{ alignItems: 'center', marginTop: '4px' }}
            >
              <div className='team-content-search'>
                <div className='screening-input-field-container'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='20'
                    height='20'
                    viewBox='0 0 20 20'
                    fill='none'
                  >
                    <path
                      d='M17.5 17.5L14.5834 14.5833M16.6667 9.58333C16.6667 13.4954 13.4954 16.6667 9.58333 16.6667C5.67132 16.6667 2.5 13.4954 2.5 9.58333C2.5 5.67132 5.67132 2.5 9.58333 2.5C13.4954 2.5 16.6667 5.67132 16.6667 9.58333Z'
                      stroke='var(--card-text)'
                      strokeWidth='1.66667'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                  <input
                    placeholder='Search Databases'
                    id='primary'
                    type='text'
                    className='screening-input-field-content'
                    name='primary'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <button
                  className='bdb-nc-sort-btn'
                  onClick={() => setShowSortMenu((v) => !v)}
                >
                  {dbSortBy === 'name' ? (
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <path d='M3 8l4-4 4 4M7 4v16M11 16h10M11 20h6M11 12h8' />
                    </svg>
                  ) : dbSortBy === 'nr_people_and_companies' ? (
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <path d='M3 8l4-4 4 4M7 4v16' />
                      <path d='M21 16l-4 4-4-4M17 20V4' />
                    </svg>
                  ) : (
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <circle cx='12' cy='12' r='10' />
                      <polyline points='12 6 12 12 16 14' />
                    </svg>
                  )}
                  <span>
                    {dbSortBy === 'created_at'
                      ? dbSortOrder === 'desc'
                        ? 'Latest'
                        : 'Oldest'
                      : dbSortBy === 'name'
                      ? dbSortOrder === 'asc'
                        ? 'A → Z'
                        : 'Z → A'
                      : dbSortBy === 'nr_people_and_companies'
                      ? 'Most Entities'
                      : 'Sort'}
                  </span>
                  <svg
                    width='12'
                    height='12'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    style={{ opacity: 0.5 }}
                  >
                    <polyline points='6 9 12 15 18 9' />
                  </svg>
                </button>
                {showSortMenu && (
                  <>
                    <div
                      style={{ position: 'fixed', inset: 0, zIndex: 99 }}
                      onClick={() => setShowSortMenu(false)}
                    />
                    <div className='bdb-nc-sort-menu'>
                      {(
                        [
                          {
                            label: 'Latest added',
                            key: 'created_at',
                            order: 'desc' as const,
                          },
                          {
                            label: 'Oldest added',
                            key: 'created_at',
                            order: 'asc' as const,
                          },
                          {
                            label: 'A → Z',
                            key: 'name',
                            order: 'asc' as const,
                          },
                          {
                            label: 'Z → A',
                            key: 'name',
                            order: 'desc' as const,
                          },
                          {
                            label: 'Most Entities',
                            key: 'nr_people_and_companies',
                            order: 'desc' as const,
                          },
                        ] as {
                          label: string;
                          key: string;
                          order: 'asc' | 'desc';
                        }[]
                      ).map((opt) => {
                        const active =
                          dbSortBy === opt.key && dbSortOrder === opt.order;
                        return (
                          <button
                            key={opt.label}
                            className={`bdb-nc-sort-menu-item${
                              active ? ' active' : ''
                            }`}
                            onClick={() => {
                              setDbSortBy(opt.key);
                              setDbSortOrder(opt.order);
                              setDbPage(1);
                              setShowSortMenu(false);
                            }}
                          >
                            {opt.label}
                            {active && (
                              <svg
                                width='14'
                                height='14'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth='2.5'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              >
                                <polyline points='20 6 9 17 4 12' />
                              </svg>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
              <div className='bdb-nc-view-toggle'>
                <button
                  className={`bdb-nc-view-btn${
                    dbViewMode === 'grid' ? ' active' : ''
                  }`}
                  onClick={() => setDbViewMode('grid')}
                  title='Grid view'
                >
                  <svg
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <rect x='3' y='3' width='7' height='7' />
                    <rect x='14' y='3' width='7' height='7' />
                    <rect x='3' y='14' width='7' height='7' />
                    <rect x='14' y='14' width='7' height='7' />
                  </svg>
                </button>
                <button
                  className={`bdb-nc-view-btn${
                    dbViewMode === 'list' ? ' active' : ''
                  }`}
                  onClick={() => setDbViewMode('list')}
                  title='List view'
                >
                  <svg
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <line x1='8' y1='6' x2='21' y2='6' />
                    <line x1='8' y1='12' x2='21' y2='12' />
                    <line x1='8' y1='18' x2='21' y2='18' />
                    <line x1='3' y1='6' x2='3.01' y2='6' />
                    <line x1='3' y1='12' x2='3.01' y2='12' />
                    <line x1='3' y1='18' x2='3.01' y2='18' />
                  </svg>
                </button>
              </div>
            </div>
            {dbsLoading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '48px 0',
                }}
              >
                <CircularProgress color='primary' />
              </div>
            ) : pagedDatabases.length === 0 ? (
              <div
                className='audit-dashboard-cards-no-ticket'
                style={{ height: 'auto', padding: '48px 0' }}
              >
                No Databases Found
              </div>
            ) : dbViewMode === 'list' ? (
              <MasterTable
                cols={<EntityDBColumns />}
                suppliersData={pagedDatabases}
                selectedItems={[]}
                setSelectedItems={setSelectedItems}
                loading={dbsLoading}
                currentPage={dbPage}
                totalPages={dbTotalPages}
                onPageChange={(_e, p) => setDbPage(p)}
                customRowComponent={(props: any) => (
                  <EntityDBRow
                    transaction={props.supplier}
                    getParagraphText={getParagraphText}
                  />
                )}
              />
            ) : (
              <div className='entity-db-grid'>
                {pagedDatabases.map((database: any) => {
                  const entityCount =
                    parseInt(database.nr_people_and_companies, 10) || 0;
                  const clearedCount = parseInt(database.nr_cleared, 10) || 0;
                  const quarantinedCount =
                    parseInt(database.nr_quarantined, 10) || 0;
                  const barredCount = parseInt(database.nr_barred, 10) || 0;
                  const highRiskUnclearedCount =
                    parseInt(database.nr_high_risk_uncleared, 10) || 0;
                  const screeningRate =
                    entityCount > 0
                      ? parseFloat(
                          (
                            (highRiskUnclearedCount / entityCount) *
                            100
                          ).toFixed(1),
                        )
                      : 0;

                  return (
                    <div
                      key={database.id}
                      style={{
                        pointerEvents: isInteractionDisabled ? 'none' : 'auto',
                        opacity: isInteractionDisabled ? 0.7 : 1,
                      }}
                    >
                      <DatabaseCard
                        id={database.id}
                        name={database.name}
                        description={database.description}
                        type={database.type}
                        screeningRate={screeningRate}
                        lastUpdated={getParagraphText(database)}
                        isProcessing={processingDatabases?.has(
                          database.id.toString(),
                        )}
                        isScreeningDelayed={
                          database.last_screening_status === 'in-progress' &&
                          Boolean(database.last_screening_delayed_at)
                        }
                        stats={[
                          {
                            label: 'Entities',
                            value: entityCount,
                            icon: (
                              <svg
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              >
                                <path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' />
                                <circle cx='9' cy='7' r='4' />
                                <path d='M22 21v-2a4 4 0 0 0-3-3.87' />
                                <path d='M16 3.13a4 4 0 0 1 0 7.75' />
                              </svg>
                            ),
                          },
                          {
                            label: 'Cleared',
                            value: clearedCount,
                            icon: (
                              <svg
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              >
                                <path d='M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z' />
                                <path d='m9 12 2 2 4-4' />
                              </svg>
                            ),
                          },
                          {
                            label: 'Flagged',
                            value: quarantinedCount + barredCount,
                            icon: (
                              <svg
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              >
                                <path d='m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z' />
                                <path d='M12 9v4' />
                                <path d='M12 17h.01' />
                              </svg>
                            ),
                          },
                        ]}
                      />
                    </div>
                  );
                })}
                <button
                  type='button'
                  className='entity-db-create-tile'
                  disabled={isInteractionDisabled}
                  onClick={() => {
                    if (!isInteractionDisabled) {
                      setShowCreateDatabaseModal(true);
                    }
                  }}
                >
                  <AddIcon style={{ fontSize: 24 }} />
                  <span>Create Database</span>
                </button>
              </div>
            )}
            {pagedDatabases.length > 0 && (
              <div
                className='hb-pagination'
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  gap: '16px',
                  flexWrap: 'wrap',
                }}
              >
                <Pagination
                  onChange={(_e: any, newPage: React.SetStateAction<number>) =>
                    setDbPage(newPage)
                  }
                  size='large'
                  count={dbTotalPages}
                  shape='rounded'
                  variant='text'
                  page={dbPage}
                />
                <PaginationJump
                  totalPages={dbTotalPages}
                  currentPage={dbPage}
                  onJump={(pageNumber) => setDbPage(pageNumber)}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'notifications' && (
        <EntityHubNotificationsTab
          groupId={groupIdForQueries}
          isInteractionDisabled={isInteractionDisabled}
        />
      )}

      <Modal
        open={showCreateDatabaseModal}
        onClose={() => setShowCreateDatabaseModal(false)}
      >
        <div style={{ outline: 'none' }}>
          <CreateDatabaseModal
            open={showCreateDatabaseModal}
            handleClose={() => setShowCreateDatabaseModal(false)}
            handleRefresh={handleRefresh}
            onDatabaseCreated={onDatabaseCreated}
            onDatabaseUploadFailed={onDatabaseUploadFailed}
            onOfferLinkEntities={onOfferLinkEntities}
            canConfigureEntityAddon={canConfigureEntityAddonOnCreate}
          />
        </div>
      </Modal>
      <DatabaseFilterModal
        open={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        statusFilters={statusFilters}
        riskFilters={riskFilters}
        hitCountRanges={hitCountRanges}
        nameStartsWith={nameStartsWith}
        hasNewResults={hasNewResultsFilter}
        lensFilters={lensFilters}
        onApply={handleApplyDatabaseFilters}
      />
      <Modal
        open={isDeletingItems}
        onClose={() => {
          setIsDeletingItems(false);
          setSelectedItems([]);
        }}
      >
        <DeleteCommonModal
          handleClose={() => {
            setIsDeletingItems(false);
            setSelectedItems([]);
          }}
          handleDelete={handleDeleteEntity}
          title={`Remove ${selectedItems.length} ${
            selectedItems.length === 1 ? 'entity' : 'entities'
          } from their Database?`}
          paragraph={
            "Each entity is removed only from the Database it is listed in here, together with its Screening History there. It stays in your team's other Databases."
          }
          confirmLabel='Remove'
          loadingLabel='Removing'
          isLoading={removeDatabaseEntitiesMutation.isPending}
        />
      </Modal>
      <Modal
        open={isChangingStatus && !showStatusConfirmModal}
        onClose={() => {
          setIsChangingStatus(false);
        }}
      >
        <ChangeStatus
          isChangingStatus={isStatusUpdating}
          handleClose={() => {
            setIsChangingStatus(false);
          }}
          curStatus={'clear'}
          changeStatus={handleStatusSelection}
        />
      </Modal>
      <Modal open={showStatusConfirmModal} onClose={closeStatusConfirmation}>
        <ConfirmStatusChangeModal
          count={selectedItems.length}
          status={pendingStatus}
          onCancel={closeStatusConfirmation}
          onConfirm={confirmStatusChange}
          isProcessing={isStatusUpdating}
        />
      </Modal>
      <Modal
        open={Boolean(pendingRestoreByFlag)}
        onClose={closeRestoreByFlagConfirmation}
      >
        <ConfirmStatusChangeModal
          title='Confirm restore'
          confirmLabel='Restore'
          count={pendingRestoreByFlagCount}
          description={pendingRestoreByFlagDescription}
          onCancel={closeRestoreByFlagConfirmation}
          onConfirm={handleRestorePreviousByFlag}
          isProcessing={loadingRestoreByFlag !== null}
          confirmDisabled={pendingRestoreByFlagCount === 0}
        />
      </Modal>
    </div>
  );
};

export default PeopleAndCompany;
